import { useSessionStore } from "features/Session"
import * as React from "react"
import useForm from "react-hook-form"
import { Link } from "react-router-dom"
import { c, t } from "ttag"

import { LoginFormFields } from "features/Login/LoginPage"
import { APPLICATION_URL } from "features/Navigation"
import { CenteredLoader, Input } from "@humanpredictiveintelligence/myqvt-library"
import { BaseProps } from "@humanpredictiveintelligence/myqvt-library"
import business from "config/business"
import * as Styles from "./LoginForm.styles"

export const LoginForm: React.FC<LoginFormProps> = (props) => {
  const authentication = useSessionStore(state => state.authentication)

  const {
    register: registerField,
    handleSubmit,
    errors: formErrors,
    reset: resetForm,
    triggerValidation: validateForm,
  } = useForm<LoginFormFields>({ defaultValues: { email: props.email, password: props.password }, mode: "onChange" })

  return (
    <Styles.Form aria-label="form" onSubmit={handleSubmit(onSubmitForm)} className={props.className}>
      <Styles.Title>{c("Login form title").t`Sign in`}</Styles.Title>
      <Styles.Fields>
        <Input
          name="email"
          innerRef={registerField({
            pattern: {
              message: t`The email format is invalid`,
              value: business.patterns.email,
            },
            required: t`This field is required`,
          })}
          label={t`Email`}
          isDisabled={props.formStatus === "processing"}
          placeholder={t`Enter your email address`}
          isErroneous={!!formErrors.email}
          hint={formErrors.email && formErrors.email.message}
          isHintErroneous
        />

        <Input
          isPassword
          name="password"
          innerRef={registerField({
            required: t`This field is required`,
          })}
          label={t`Password`}
          isDisabled={props.formStatus === "processing"}
          placeholder={t`Enter your password`}
          isErroneous={!!formErrors.password}
          isHintErroneous={!!formErrors.password}
          hint={(
            <Styles.PasswordHint>
              {formErrors.password && formErrors.password.message}
              <Link to={APPLICATION_URL.changePasswordRequest()}>{t`Forgotten password?`}</Link>
            </Styles.PasswordHint>
          )}
        />
      </Styles.Fields>

      <Styles.StatusContainer>
        {props.formStatus === "failed" && (
          <Styles.ErrorMessage>
            {authentication.reason ?? t`Your credentials are invalid.`}
          </Styles.ErrorMessage>
        )}

        {props.formStatus === "processing" && <CenteredLoader isTransparent />}
      </Styles.StatusContainer>

      <Styles.SubmitButton
        submit
        disabled={props.formStatus === "processing"}
      >
        {t`Sign in`}
      </Styles.SubmitButton>
    </Styles.Form>
  )

  /**
   * Called when the form is submitted
   * @param values Login form fields values
   */

  function onSubmitForm(values: LoginFormFields) {
    props.onSubmit(values)

    if (props.formStatus === "failed") {
      resetForm(values)
      validateForm(
        (Object.keys(values) as (keyof LoginFormFields)[])
          .map(fieldName => ({ name: fieldName })),
      )
    }
  }
}

export interface LoginFormProps extends BaseProps {
  /** Whether the form is loading */
  isLoading?: boolean,

  /** Autofill the email field with the email */
  email?: string,

  /** Autofill the password field with the password */
  password?: string,

  formStatus?: string,

  onSubmit: (values: LoginFormFields) => void,
}
